import { useEffect, useMemo, useState } from 'react';
import {
  BellRing,
  Link2,
  Pencil,
  QrCode,
  Send,
  Trash2,
  UserPlus,
  Users,
  Upload,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { formatAttendanceBreakdown, invitePreviewUrl } from '@peles/shared';
import type { InviteCampaign, InviteRecipient } from '@peles/shared';
import {
  EntityModal,
  EntityList,
  StatusBadge,
  SearchableListSelect,
  ShareLinkButton,
} from '../common';
import type { EntityListColumn } from '../common';
import { useDialog } from '../../context/DialogContext';
import {
  useInviteDesigns,
  useCreateInviteDesign,
  useUploadDesignPage,
  useDeleteDesignPage,
  useSetCampaignPublicLink,
  useCreateInviteCampaign,
  useUpdateInviteCampaign,
  useCampaignRecipients,
  useAddCampaignRecipients,
  useRemoveCampaignRecipient,
  useSendInviteCampaign,
  useInviteCampaignStats,
  useSubEvents,
  useRemindInviteCampaign,
  useScheduleInviteCampaign,
} from '../../hooks/useInvites';
import { useTimelines } from '../../hooks/useEventOps';
import { DEFAULT_ENVELOPE_COLOR } from './EnvelopeReveal';
import { isPdfFile, pdfToImageFiles } from '../../utils/pdfToImages';
import { AddRecipientsModal } from './AddRecipientsModal';
import { RecipientEditModal } from './RecipientEditModal';
import { RsvpQuestionsSection } from './RsvpQuestionsSection';
import { MealOptionsSection } from './MealOptionsSection';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_ACCENT_COLOR = '#1a365d';

interface CampaignModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Existing campaign to edit; null/undefined to create. */
  campaign?: InviteCampaign | null;
  /**
   * Client-portal mode: campaign fields are read-only and producer-only
   * sections (design upload, meals, questions, remind, schedule) are hidden.
   * Recipients, send, stats, and RSVP links/QRs stay available.
   */
  clientMode?: boolean;
}

const RECIPIENT_STATUS_COLORS: Record<string, string> = {
  PENDING: '#94a3b8',
  QUEUED: '#94a3b8',
  SENT: '#3b82f6',
  DELIVERED: '#3b82f6',
  OPENED: '#8b5cf6',
  RESPONDED: '#22c55e',
  BOUNCED: '#ef4444',
  FAILED: '#ef4444',
};

const RESPONSE_COLORS: Record<string, string> = {
  YES: '#22c55e',
  NO: '#ef4444',
  MAYBE: '#f59e0b',
  PENDING: '#94a3b8',
};

function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function CampaignModal({
  open,
  onClose,
  eventId,
  campaign,
  clientMode = false,
}: CampaignModalProps) {
  const isEdit = !!campaign;
  const { alert, confirm } = useDialog();

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [deadline, setDeadline] = useState('');
  const [designId, setDesignId] = useState<string>('');
  const [allowMaybe, setAllowMaybe] = useState(true);
  const [allowPartyEdit, setAllowPartyEdit] = useState(true);
  const [showHebrewDate, setShowHebrewDate] = useState(false);
  const [showSeatFinder, setShowSeatFinder] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_BACKGROUND_COLOR);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR);
  const [includeEventLogo, setIncludeEventLogo] = useState(false);
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [envelopeSealLogo, setEnvelopeSealLogo] = useState(false);
  const [envelopeColor, setEnvelopeColor] = useState(DEFAULT_ENVELOPE_COLOR);
  const [sealColor, setSealColor] = useState(DEFAULT_ACCENT_COLOR);
  const [envelopeMessage, setEnvelopeMessage] = useState('');
  const [rsvpEnabled, setRsvpEnabled] = useState(true);
  const [allowPublicRsvp, setAllowPublicRsvp] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineId, setTimelineId] = useState<string>('');
  const [qrRecipient, setQrRecipient] = useState<InviteRecipient | null>(null);
  const [editingRecipient, setEditingRecipient] = useState<InviteRecipient | null>(null);
  const [addingRecipients, setAddingRecipients] = useState(false);
  const [rsvpEventIds, setRsvpEventIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? '');
    setSubject(campaign?.subject ?? '');
    setMessage(campaign?.message ?? '');
    setDeadline(toDateInputValue(campaign?.rsvpDeadline));
    setDesignId(campaign?.designId ?? '');
    setAllowMaybe(campaign?.settings?.allowMaybe !== false);
    setAllowPartyEdit(campaign?.settings?.allowPartyEdit !== false);
    setShowHebrewDate(campaign?.settings?.showHebrewDate === true);
    setShowSeatFinder(campaign?.settings?.showSeatFinder === true);
    setBackgroundColor(campaign?.settings?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR);
    setAccentColor(campaign?.settings?.accentColor ?? DEFAULT_ACCENT_COLOR);
    setIncludeEventLogo(campaign?.settings?.includeEventLogo === true);
    setShowEnvelope(campaign?.settings?.showEnvelope !== false);
    setEnvelopeSealLogo(campaign?.settings?.envelopeSealLogo === true);
    setEnvelopeColor(campaign?.settings?.envelopeColor ?? DEFAULT_ENVELOPE_COLOR);
    setSealColor(
      campaign?.settings?.sealColor ?? campaign?.settings?.accentColor ?? DEFAULT_ACCENT_COLOR,
    );
    setEnvelopeMessage(campaign?.settings?.envelopeMessage ?? '');
    setRsvpEnabled(campaign?.settings?.rsvpEnabled !== false);
    setAllowPublicRsvp(campaign?.settings?.allowPublicRsvp !== false);
    setShowTimeline(campaign?.settings?.showTimeline === true);
    setTimelineId(campaign?.timelineId ?? '');
    setRsvpEventIds(campaign?.rsvpEventIds ?? []);
  }, [open, campaign]);

  const { data: subEvents = [] } = useSubEvents(eventId);
  const { data: timelines = [] } = useTimelines(eventId);

  const toggleSubEvent = (id: string) => {
    setRsvpEventIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  };

  const { data: designs = [] } = useInviteDesigns({ eventId });
  const createDesign = useCreateInviteDesign();
  const uploadPage = useUploadDesignPage();
  const removePage = useDeleteDesignPage();
  const setPublicLink = useSetCampaignPublicLink();
  const createCampaign = useCreateInviteCampaign();
  const updateCampaign = useUpdateInviteCampaign();

  const { data: recipients = [], isLoading: recipientsLoading } = useCampaignRecipients(
    campaign?.id,
  );
  const { data: stats } = useInviteCampaignStats(campaign?.id);
  const addRecipients = useAddCampaignRecipients();
  const removeRecipient = useRemoveCampaignRecipient();
  const sendCampaign = useSendInviteCampaign();
  const remindCampaign = useRemindInviteCampaign();
  const scheduleCampaign = useScheduleInviteCampaign();

  const handleSave = async () => {
    if (!name.trim()) {
      await alert({ message: 'Please enter a campaign name.', variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const shared = {
        name: name.trim(),
        subject: subject || undefined,
        message: message || undefined,
        rsvpDeadline: deadline ? new Date(`${deadline}T23:59:59.000Z`).toISOString() : null,
        designId: designId || null,
        rsvpEventIds,
        timelineId: timelineId || null,
        settings: {
          allowMaybe,
          allowPartyEdit,
          showHebrewDate,
          showSeatFinder,
          backgroundColor,
          accentColor,
          includeEventLogo,
          showEnvelope,
          envelopeSealLogo,
          envelopeColor,
          sealColor,
          envelopeMessage: envelopeMessage.trim() || undefined,
          rsvpEnabled,
          allowPublicRsvp,
          showTimeline,
        },
      };
      if (isEdit && campaign) {
        await updateCampaign.mutateAsync({ id: campaign.id, updates: shared });
      } else {
        await createCampaign.mutateAsync({ eventId, ...shared });
      }
      onClose();
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to save campaign',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadArtwork = async (file: File) => {
    try {
      // A PDF is rasterized here, one image per page, so the invitation is
      // always artwork the guest's browser and the invite email can render.
      const uploads = isPdfFile(file) ? await pdfToImageFiles(file) : [file];
      if (uploads.length === 0) throw new Error('That PDF has no pages to import');
      let targetId = designId;
      if (!targetId) {
        const design = await createDesign.mutateAsync({
          eventId,
          name: name.trim() || file.name,
        });
        targetId = design.id;
      }
      // Sequential so the pages land in the order they appear in the file.
      for (const [index, upload] of uploads.entries()) {
        try {
          await uploadPage.mutateAsync({ id: targetId, file: upload });
          // Claim a freshly created design only once it actually holds
          // artwork: if the very first page fails there is nothing to show,
          // so the campaign must not be left pointing at an empty design.
          if (index === 0) setDesignId(targetId);
        } catch (err) {
          // Pages that already uploaded stay attached (and stay visible in the
          // page list) — abandoning them would orphan uploaded artwork. Say
          // exactly how far the import got so a half-imported invitation is
          // never a surprise.
          const reason = err instanceof Error ? err.message : 'Failed to upload artwork';
          throw new Error(
            index === 0
              ? reason
              : `${reason}. Only the first ${index} of ${uploads.length} pages were added — ` +
                  'remove them and try again.',
          );
        }
      }
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to upload artwork',
        variant: 'error',
      });
    }
  };

  const handleRemovePage = async (pageId: string) => {
    if (!designId) return;
    const ok = await confirm({
      message: 'Remove this page from the invitation?',
      variant: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await removePage.mutateAsync({ designId, pageId });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to remove page',
        variant: 'error',
      });
    }
  };

  const handleTogglePublicLink = async (enabled: boolean) => {
    if (!campaign) return;
    if (!enabled) {
      const ok = await confirm({
        message: 'Revoke the shared link? Anyone still holding it will get a "not found" page.',
        variant: 'danger',
        confirmLabel: 'Revoke',
      });
      if (!ok) return;
    }
    try {
      await setPublicLink.mutateAsync({ campaignId: campaign.id, enabled });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to update the shared link',
        variant: 'error',
      });
    }
  };

  const handleAddAllGuests = async () => {
    if (!campaign) return;
    const result = await addRecipients.mutateAsync({
      campaignId: campaign.id,
      input: { allGuests: true },
    });
    await alert({
      message: `Added ${result.added} guest${result.added === 1 ? '' : 's'} to the campaign.`,
      variant: 'success',
    });
  };

  const handleSend = async () => {
    if (!campaign) return;
    const emailable = recipients.filter(
      (r) => r.guestEmail && ['PENDING', 'QUEUED', 'FAILED'].includes(r.status),
    ).length;
    const ok = await confirm({
      message: `Send this invitation to ${emailable} recipient${emailable === 1 ? '' : 's'} by email?`,
      confirmLabel: 'Send',
    });
    if (!ok) return;
    try {
      const result = await sendCampaign.mutateAsync(campaign.id);
      await alert({
        message:
          `Sent ${result.sent} invitation${result.sent === 1 ? '' : 's'}` +
          (result.failed ? `, ${result.failed} failed` : '') +
          (result.skipped ? `, ${result.skipped} skipped (no email)` : '') +
          '.',
        variant: result.failed ? 'warning' : 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to send campaign',
        variant: 'error',
      });
    }
  };

  const handleRemind = async () => {
    if (!campaign) return;
    const ok = await confirm({
      message: 'Send a reminder email to everyone who has not responded yet?',
      confirmLabel: 'Remind',
    });
    if (!ok) return;
    try {
      const result = await remindCampaign.mutateAsync(campaign.id);
      await alert({
        message: `Reminded ${result.reminded} guest${result.reminded === 1 ? '' : 's'} (${result.emailsSent} emails).`,
        variant: 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to send reminders',
        variant: 'error',
      });
    }
  };

  const handleSchedule = async (value: string) => {
    if (!campaign) return;
    try {
      await scheduleCampaign.mutateAsync({
        campaignId: campaign.id,
        scheduledAt: value ? new Date(value).toISOString() : null,
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to schedule campaign',
        variant: 'error',
      });
    }
  };

  // Links handed to guests are always the share URL, never the raw SPA path:
  // it carries the event's Open Graph tags, so a link pasted into WhatsApp
  // unfurls with the event name, date and logo before it redirects the guest
  // to their own invitation page.
  const recipientShareUrl = (recipient: InviteRecipient) =>
    invitePreviewUrl(window.location.origin, recipient.token);

  const shareMessage = `${subject || name || 'You are invited!'} —`;

  const handleRemoveRecipient = async (recipient: InviteRecipient) => {
    if (!campaign) return;
    const ok = await confirm({
      message: `Remove ${recipient.guestName ?? 'this guest'} from the campaign?`,
      variant: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    await removeRecipient.mutateAsync({ campaignId: campaign.id, recipientId: recipient.id });
  };

  const recipientColumns: EntityListColumn<InviteRecipient>[] = useMemo(
    () => [
      {
        key: 'guest',
        header: 'Guest',
        render: (r) => (
          <div>
            <div className="font-medium text-gray-900">{r.guestName ?? '—'}</div>
            <div className="text-xs text-gray-500">{r.guestEmail ?? 'No email'}</div>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <StatusBadge label={r.status} color={RECIPIENT_STATUS_COLORS[r.status]} />,
      },
      {
        key: 'response',
        header: 'RSVP',
        render: (r) => (
          <StatusBadge
            label={r.response ?? 'PENDING'}
            color={RESPONSE_COLORS[r.response ?? 'PENDING']}
          />
        ),
      },
      {
        key: 'attendants',
        header: 'Attendants',
        align: 'center',
        render: (r) =>
          r.attendingCount ? (
            <div>
              <div className="font-medium text-gray-900">{r.attendingCount}</div>
              <div className="text-xs text-gray-500">
                {formatAttendanceBreakdown(r.adultCount ?? 0, r.kidCount ?? 0)}
              </div>
            </div>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <div className="flex items-center justify-end gap-1">
            <ShareLinkButton
              compact
              url={recipientShareUrl(r)}
              text={shareMessage}
              title={r.guestName ?? 'Invitation'}
              label={r.guestName ?? 'guest'}
              phone={r.guestPhone}
            />
            <button
              type="button"
              title="Edit RSVP"
              aria-label={`Edit RSVP for ${r.guestName ?? 'guest'}`}
              onClick={() => setEditingRecipient(r)}
              className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Show QR code"
              aria-label={`Show QR code for ${r.guestName ?? 'guest'}`}
              onClick={() => setQrRecipient(r)}
              className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
            >
              <QrCode className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Remove recipient"
              aria-label={`Remove ${r.guestName ?? 'guest'}`}
              onClick={() => void handleRemoveRecipient(r)}
              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [campaign?.id, shareMessage],
  );

  const selectedDesign = designs.find((d) => d.id === designId);
  const designPages = selectedDesign?.pages ?? [];
  const publicShareUrl = campaign?.publicToken
    ? invitePreviewUrl(window.location.origin, campaign.publicToken)
    : null;

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Campaign' : 'New Invite Campaign'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {!clientMode && (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Campaign'}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              id="campaign-name"
              type="text"
              value={name}
              disabled={clientMode}
              onChange={(e) => setName(e.target.value)}
              placeholder="Main Invitation"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label htmlFor="campaign-deadline" className="block text-sm font-medium text-gray-700">
              RSVP Deadline
            </label>
            <input
              id="campaign-deadline"
              type="date"
              value={deadline}
              disabled={clientMode}
              onChange={(e) => setDeadline(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="campaign-subject" className="block text-sm font-medium text-gray-700">
            Email Subject
          </label>
          <input
            id="campaign-subject"
            type="text"
            value={subject}
            disabled={clientMode}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="You're invited!"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        <div>
          <label htmlFor="campaign-message" className="block text-sm font-medium text-gray-700">
            Message
          </label>
          <textarea
            id="campaign-message"
            value={message}
            disabled={clientMode}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="We would be honored to have you join us…"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {!clientMode && (
          <div>
            <span className="block text-sm font-medium text-gray-700">Invitation Artwork</span>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <div className="min-w-56">
                <SearchableListSelect
                  items={designs}
                  value={designId}
                  onChange={(value) => setDesignId(typeof value === 'string' ? value : '')}
                  getValue={(design) => design.id}
                  getLabel={(design) => design.name}
                  getSubtitle={(design) =>
                    design.pages?.length
                      ? `${design.pages.length} page${design.pages.length === 1 ? '' : 's'}`
                      : null
                  }
                  placeholder="No artwork"
                  ariaLabel="Invitation design"
                  emptyMessage="No artwork uploaded yet"
                  clearable
                />
              </div>
              <label className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                <Upload className="w-4 h-4" />
                {designPages.length > 0 ? 'Add page' : 'Upload'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleUploadArtwork(file);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            {designPages.length > 0 && (
              <>
                <div className="mt-2 flex flex-wrap gap-3">
                  {designPages.map((page, index) => (
                    <div key={page.id} className="relative">
                      {page.artworkUrl && (
                        <img
                          src={page.artworkUrl}
                          alt={`Invitation page ${index + 1}`}
                          className="max-h-40 rounded-lg border border-gray-200"
                        />
                      )}
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
                        Page {index + 1}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove invitation page ${index + 1}`}
                        onClick={() => void handleRemovePage(page.id)}
                        className="absolute top-1 right-1 p-1 rounded bg-white/90 text-gray-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Guests flip through the pages on the invitation page — the first page is used for
                  the email and the link preview. A PDF is converted to images, one page each.
                </p>
              </>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-6">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allowMaybe}
              onChange={(e) => setAllowMaybe(e.target.checked)}
              disabled={clientMode}
            />
            Allow &quot;Maybe&quot; responses
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allowPartyEdit}
              onChange={(e) => setAllowPartyEdit(e.target.checked)}
              disabled={clientMode}
            />
            Guests can edit party size
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showHebrewDate}
              onChange={(e) => setShowHebrewDate(e.target.checked)}
              disabled={clientMode}
            />
            Show Hebrew date
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showSeatFinder}
              onChange={(e) => setShowSeatFinder(e.target.checked)}
              disabled={clientMode}
            />
            Enable seat finder
          </label>
        </div>

        {!clientMode && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <span className="block text-sm font-medium text-gray-700">
              Appearance &amp; details
            </span>
            <div className="flex flex-wrap items-end gap-5">
              <div>
                <label
                  htmlFor="campaign-bg-color"
                  className="block text-xs font-medium text-gray-600"
                >
                  Background color
                </label>
                <input
                  id="campaign-bg-color"
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="mt-1 h-9 w-16 rounded border border-gray-300"
                />
              </div>
              <div>
                <label
                  htmlFor="campaign-accent-color"
                  className="block text-xs font-medium text-gray-600"
                >
                  Accent color
                </label>
                <input
                  id="campaign-accent-color"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="mt-1 h-9 w-16 rounded border border-gray-300"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
                  setAccentColor(DEFAULT_ACCENT_COLOR);
                  setEnvelopeColor(DEFAULT_ENVELOPE_COLOR);
                  setSealColor(DEFAULT_ACCENT_COLOR);
                }}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                Reset colors
              </button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={includeEventLogo}
                  onChange={(e) => setIncludeEventLogo(e.target.checked)}
                />
                Include event logo
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showEnvelope}
                  onChange={(e) => setShowEnvelope(e.target.checked)}
                />
                Envelope opening animation
              </label>
              {showEnvelope && (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={envelopeSealLogo}
                    onChange={(e) => setEnvelopeSealLogo(e.target.checked)}
                  />
                  Seal the envelope with the event logo
                </label>
              )}
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={rsvpEnabled}
                  onChange={(e) => setRsvpEnabled(e.target.checked)}
                />
                Collect RSVPs
              </label>
            </div>

            {/* The envelope colors belong to the envelope, but the letter text also
                titles the page — so it stays editable when the envelope is off,
                otherwise a heading could be set and then become unreachable. */}
            <div className="flex flex-wrap items-end gap-5">
              {showEnvelope && (
                <>
                  <div>
                    <label
                      htmlFor="campaign-envelope-color"
                      className="block text-xs font-medium text-gray-600"
                    >
                      Envelope color
                    </label>
                    <input
                      id="campaign-envelope-color"
                      type="color"
                      value={envelopeColor}
                      onChange={(e) => setEnvelopeColor(e.target.value)}
                      className="mt-1 h-9 w-16 rounded border border-gray-300"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="campaign-seal-color"
                      className="block text-xs font-medium text-gray-600"
                    >
                      Seal color
                    </label>
                    <input
                      id="campaign-seal-color"
                      type="color"
                      value={sealColor}
                      onChange={(e) => setSealColor(e.target.value)}
                      className="mt-1 h-9 w-16 rounded border border-gray-300"
                    />
                  </div>
                </>
              )}
              <div className="flex-1 min-w-56">
                <label
                  htmlFor="campaign-envelope-message"
                  className="block text-xs font-medium text-gray-600"
                >
                  {showEnvelope ? 'Text on the letter' : 'Invitation heading'}
                </label>
                <input
                  id="campaign-envelope-message"
                  type="text"
                  maxLength={200}
                  value={envelopeMessage}
                  onChange={(e) => setEnvelopeMessage(e.target.value)}
                  placeholder="Defaults to the event name"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {showEnvelope
                    ? "Shown as the envelope opens, and as the invitation's heading in place of the event name."
                    : "Shown as the invitation's heading in place of the event name."}
                </p>
              </div>
            </div>
            {!rsvpEnabled && (
              <p className="text-xs text-gray-500">
                Information only — guests see the invitation and event details, with no response
                form.
              </p>
            )}
            <div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showTimeline}
                  onChange={(e) => setShowTimeline(e.target.checked)}
                />
                Show event timeline
              </label>
              {showTimeline && (
                <div className="mt-2">
                  <SearchableListSelect
                    items={timelines}
                    value={timelineId}
                    onChange={(v) => setTimelineId(typeof v === 'string' ? v : '')}
                    getValue={(tl) => tl.id}
                    getLabel={(tl) => tl.title || 'Untitled timeline'}
                    placeholder="Select a timeline…"
                    ariaLabel="Timeline to display"
                    clearable
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {subEvents.length > 0 && (
          <div>
            <span className="block text-sm font-medium text-gray-700">Also collect RSVPs for</span>
            <div className="mt-1 flex flex-wrap gap-3">
              {subEvents.map((subEvent) => (
                <label
                  key={subEvent.id}
                  className="inline-flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={rsvpEventIds.includes(subEvent.id)}
                    onChange={() => toggleSubEvent(subEvent.id)}
                    disabled={clientMode}
                  />
                  {subEvent.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {isEdit && campaign && !clientMode && (
          <div className="pt-4 border-t border-gray-200 space-y-4">
            <MealOptionsSection eventId={eventId} />
            <RsvpQuestionsSection campaignId={campaign.id} />
          </div>
        )}

        {isEdit && campaign && (
          <div className="pt-4 border-t border-gray-200 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Shared invitation link</h3>
              <button
                type="button"
                onClick={() => void handleTogglePublicLink(!campaign.publicToken)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Link2 className="w-4 h-4" />
                {campaign.publicToken ? 'Revoke link' : 'Create link'}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              One link for everyone, with no guest name on it. Send it on WhatsApp and it previews
              with the event name, date and logo.
            </p>
            {!clientMode && (
              <>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={allowPublicRsvp}
                    disabled={!rsvpEnabled}
                    onChange={(e) => setAllowPublicRsvp(e.target.checked)}
                  />
                  Accept RSVPs from this link
                </label>
                <p className="text-xs text-gray-500">
                  {!rsvpEnabled
                    ? 'Turn on "Collect RSVPs" above to accept responses from the shared link.'
                    : allowPublicRsvp
                      ? 'Whoever opens it gives their name, email and party size, and is added to the guest list.'
                      : 'The shared link shows the invitation only — no response form.'}
                </p>
              </>
            )}
            {publicShareUrl && (
              <>
                <ShareLinkButton
                  url={publicShareUrl}
                  text={shareMessage}
                  title={campaign.name}
                  className="pt-1"
                />
                <p className="text-xs text-gray-400 break-all">{publicShareUrl}</p>
              </>
            )}
          </div>
        )}

        {isEdit && campaign && (
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Recipients ({recipients.length})
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAddingRecipients(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Guests
                </button>
                <button
                  type="button"
                  onClick={() => void handleAddAllGuests()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Users className="w-4 h-4" />
                  Add All
                </button>
                {campaign.status === 'SENT' && !clientMode && (
                  <button
                    type="button"
                    onClick={() => void handleRemind()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <BellRing className="w-4 h-4" />
                    Remind
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </div>

            {campaign.status !== 'SENT' && !clientMode && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <label htmlFor="campaign-schedule" className="font-medium">
                  Schedule send
                </label>
                <input
                  id="campaign-schedule"
                  type="datetime-local"
                  defaultValue={
                    campaign.scheduledAt
                      ? new Date(campaign.scheduledAt).toISOString().slice(0, 16)
                      : ''
                  }
                  onChange={(e) => void handleSchedule(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
                {campaign.status === 'SCHEDULED' && (
                  <StatusBadge label="SCHEDULED" color="#3b82f6" />
                )}
              </div>
            )}

            {stats && (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  {(
                    [
                      ['Sent', stats.sent],
                      ['Opened', stats.opened],
                      ['Yes', stats.yes],
                      ['No', stats.no],
                      ['Maybe', stats.maybe],
                      ['Pending', stats.pending],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="bg-gray-50 rounded-lg py-2">
                      <div className="text-lg font-semibold text-gray-900">{value}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  ))}
                </div>
                {/* The head count is what the caterer and the seating chart need —
                    "12 yes" is not 12 chairs. */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg py-2 px-3 text-center">
                  <div className="text-lg font-semibold text-primary-800">
                    {stats.attendingAdults + stats.attendingKids}
                  </div>
                  <div className="text-xs text-primary-700">
                    Attendants
                    {formatAttendanceBreakdown(stats.attendingAdults, stats.attendingKids)
                      ? ` · ${formatAttendanceBreakdown(stats.attendingAdults, stats.attendingKids)}`
                      : ''}
                  </div>
                </div>
                {(stats.perEvent?.length ?? 0) > 1 && (
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {stats.perEvent.slice(1).map((eventStats) => (
                      <div key={eventStats.eventId} className="flex justify-between gap-2">
                        <span className="truncate">{eventStats.eventName}</span>
                        <span className="shrink-0">
                          {eventStats.yes} yes · {eventStats.no} no · {eventStats.maybe} maybe (
                          {eventStats.attendingAdults + eventStats.attendingKids} attending
                          {formatAttendanceBreakdown(
                            eventStats.attendingAdults,
                            eventStats.attendingKids,
                          )
                            ? `: ${formatAttendanceBreakdown(
                                eventStats.attendingAdults,
                                eventStats.attendingKids,
                              )}`
                            : ''}
                          )
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {(stats.mealCounts?.length ?? 0) > 0 && (
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">Meals: </span>
                    {stats.mealCounts.map((meal) => `${meal.name} × ${meal.count}`).join(' · ')}
                    {stats.unassignedMeals > 0 && ` · ${stats.unassignedMeals} unassigned`}
                  </div>
                )}
              </>
            )}

            <EntityList
              items={recipients}
              columns={recipientColumns}
              getKey={(r) => r.id}
              isLoading={recipientsLoading}
            />
          </div>
        )}
      </div>

      <AddRecipientsModal
        open={addingRecipients}
        onClose={() => setAddingRecipients(false)}
        eventId={eventId}
        campaignId={campaign?.id ?? ''}
        existingGuestIds={recipients.map((r) => r.guestId)}
      />

      <RecipientEditModal
        open={!!editingRecipient}
        onClose={() => setEditingRecipient(null)}
        campaignId={campaign?.id ?? ''}
        recipient={editingRecipient}
      />

      <EntityModal
        open={!!qrRecipient}
        onClose={() => setQrRecipient(null)}
        title={qrRecipient?.guestName ?? 'RSVP QR Code'}
        size="xs"
        presentation="centered"
      >
        {qrRecipient && (
          <div className="flex flex-col items-center gap-3 py-2">
            <QRCode
              value={`${window.location.origin}/rsvp/${qrRecipient.token}`}
              size={192}
              aria-label="RSVP QR code"
            />
            <p className="text-xs text-gray-500 break-all text-center">
              {`${window.location.origin}/rsvp/${qrRecipient.token}`}
            </p>
            <p className="text-xs text-gray-400 text-center">
              Scan to open the RSVP page — also used for check-in at the door.
            </p>
          </div>
        )}
      </EntityModal>
    </EntityModal>
  );
}
